import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Button,
  Breadcrumbs,
  Drawer,
  IconButton
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import Header from '../../components/Header/Header';
import FiltersSidebar from '../../components/FiltersSidebar/FiltersSidebar';
import HotelCardHorizontal from '../../components/HotelCardHorizontal/HotelCardHorizontal';
import g2TravelApi from '../../services/g2TravelApi';
import Footer from '../../components/Footer/Footer';
import {
  getHotelStarRating,
  hotelMatchesReviewScoreFilters,
  getHotelGuestScoreForFilter,
  getHotelSearchBlob,
  hotelMatchesKeywordFacility,
} from '../../lib/hotelFilterUtils';
import { buildHotelDetailPath } from '../../lib/hotelRoutes';
import {
  buildHotelListingFetchKey,
  getHotelListingCache,
  listingCacheHasHotelPrices,
  mergeHotelListingCache,
} from '../../lib/hotelSearchListingCache';
import { useCurrency } from '../../context/CurrencyContext';
import { getCurrencyForNationality } from '../../lib/currency';
import './Hotels.css';

const hotelPrimaryId = (hotel) => {
  const id = hotel?.id ?? hotel?.propertyId ?? hotel?.code;
  if (id == null || String(id).trim() === '') return null;
  return String(id).trim();
};

const MEAL_KEYWORDS = {
  breakfast: [
    'breakfast included',
    'including breakfast',
    'bed and breakfast',
    'buffet breakfast',
    'continental breakfast',
    'b&b',
  ],
  breakfastDinner: ['half board', 'full board', 'dinner included', 'breakfast and dinner', 'breakfast & dinner'],
  kitchen: ['kitchen', 'kitchenette', 'self-catering', 'self catering', 'cooking facilities'],
};

const TRAVEL_GROUP_KEYWORDS = {
  petFriendly: ['pet friendly', 'pets allowed', 'dogs allowed', 'pets welcome'],
  adultsOnly: ['adults only', 'adult only', '18+', 'no children'],
  lgbtqFriendly: ['lgbtq', 'lgbt+', 'travel proud', 'lgbt friendly'],
};

const BED_KEYWORDS = {
  double: ['double bed', 'queen bed', 'king bed', 'double room'],
  twin: ['twin bed', 'two single', 'twin room', 'two beds'],
};

const FUN_THING_KEYWORDS = {
  liveMusic: ['live music', 'entertainment', 'performance', 'concert'],
  massage: ['massage', 'spa treatment'],
  bicycleRental: ['bicycle', 'bike rental', 'cycling'],
  fitness: ['fitness', 'gym', 'workout'],
  fitnessCenter: ['fitness center', 'fitness centre', 'gym', 'health club'],
};

const Hotels = ({ onGoHome, onNavigate, searchParams: propsSearchParams = {} }) => {
  const { setSelectedCurrency } = useCurrency();
  const DEFAULT_MAX_PRICE = 20000;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get search params from navigation state or props
  const stateSearchParams = location.state?.searchParams || null;
  const searchParams = stateSearchParams || propsSearchParams;

  const mountListingFetchKey = buildHotelListingFetchKey(searchParams);
  const mountListingCache = getHotelListingCache(mountListingFetchKey);
  const mountHasCachedListing =
    mountListingCache != null && Array.isArray(mountListingCache.hotels);

  const [hotels, setHotels] = useState(() => mountListingCache?.hotels ?? []);
  const [hotelPrices, setHotelPrices] = useState(() => mountListingCache?.hotelPrices ?? {});
  const [loading, setLoading] = useState(() => !mountHasCachedListing);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [error, setError] = useState(() => mountListingCache?.error ?? null);
  const [sortBy, setSortBy] = useState('recommended');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    priceRange: [0, DEFAULT_MAX_PRICE],
    nationality: searchParams?.nationality || 'GB',
    propertyType: [],
    starRating: [],
    reviewScore: [],
    amenities: {},
    facilities: {},
    landmarks: [],
    bedPreference: [],
    meals: [],
    neighborhood: [],
    brands: [],
    travelGroup: [],
    distance: [],
    funThings: [],
    certifications: [],
    propertyAccessibility: {},
    roomAccessibility: {},
    roomFacilities: {}
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const lastFetchKeyRef = useRef(
    mountHasCachedListing ? mountListingFetchKey : ''
  );
  const cityLookupCacheRef = useRef({});

  useEffect(() => {
    const incomingNationality = searchParams?.nationality || 'GB';
    setFilters((prev) =>
      prev.nationality === incomingNationality ? prev : { ...prev, nationality: incomingNationality }
    );
  }, [searchParams?.nationality]);

  useEffect(() => {
    const fetchKey = buildHotelListingFetchKey(searchParams);
    const cached = getHotelListingCache(fetchKey);

    if (cached && Array.isArray(cached.hotels)) {
      // Returning from hotel detail: state already hydrated from cache on mount — skip duplicate work
      // and do not re-run dozens of price API calls when we already have prices in memory.
      if (lastFetchKeyRef.current === fetchKey) {
        const checkInForPrices =
          searchParams?.checkInDate || searchParams?.checkIn || '';
        if (
          !listingCacheHasHotelPrices(cached) &&
          String(checkInForPrices).trim() !== ''
        ) {
          void fetchHotelPrices(cached.hotels, fetchKey);
        }
        return;
      }
      setHotels(cached.hotels);
      setHotelPrices(cached.hotelPrices || {});
      setError(cached.error ?? null);
      setLoading(false);
      lastFetchKeyRef.current = fetchKey;
      const checkInForPrices =
        searchParams?.checkInDate || searchParams?.checkIn || '';
      if (
        !listingCacheHasHotelPrices(cached) &&
        String(checkInForPrices).trim() !== ''
      ) {
        void fetchHotelPrices(cached.hotels, fetchKey);
      }
      return;
    }

    // Avoid duplicate fetches in development/StrictMode while the first request is still in flight.
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;

    window.scrollTo(0, 0);
    fetchHotels(fetchKey);
    // fetchHotels reads latest searchParams from closure; listing only these keys avoids duplicate loads.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional param-scoped refetch
  }, [searchParams?.cityId, searchParams?.propertyId, searchParams?.location, searchParams?.searchTarget]);

  useEffect(() => {
    if (!Array.isArray(hotels) || hotels.length === 0) return;
    const fetchKey = buildHotelListingFetchKey({ ...searchParams, nationality: filters.nationality });
    const derivedCurrency = getCurrencyForNationality(filters.nationality);
    setSelectedCurrency(derivedCurrency);
    void fetchHotelPrices(hotels, fetchKey, derivedCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh prices only when nationality changes
  }, [filters.nationality]);

  const normalizeText = (value) => (value || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

  /**
   * When the user types a property name (e.g. "Fairmont Dubai"), pick a single hotel id from
   * the catalog so we open that property instead of fuzzy-matching random fields.
   */
  const findBestHotelPropertyIdFromQuery = (raw, hotelList) => {
    const q = (raw || '').trim().toLowerCase();
    if (!q || q.length < 3 || !Array.isArray(hotelList) || hotelList.length === 0) {
      return '';
    }

    const normQ = normalizeText(raw);
    const tokens = q.split(/\s+/).filter((t) => t.length >= 2);

    const scored = [];
    for (const h of hotelList) {
      const name = (h.name || h.propertyName || h.hotelName || '').toString().trim();
      if (!name) continue;
      const n = name.toLowerCase();
      const normN = normalizeText(name);
      const id = h.id ?? h.code ?? h.propertyId;
      if (id == null || String(id).trim() === '') continue;

      let score = 0;
      if (n === q) score = 1000;
      else if (normN === normQ) score = 950;
      else if (n.startsWith(q)) score = 850;
      else if (normQ.length >= 6 && normN.includes(normQ)) score = 720;
      else if (tokens.length >= 2 && tokens.every((t) => n.includes(t))) {
        score = 500 + 15 * tokens.length;
      }

      if (score > 0) {
        scored.push({ id: String(id).trim(), score, name });
      }
    }

    scored.sort((a, b) => b.score - a.score);
    if (scored.length === 0) return '';

    const top = scored[0];
    const tied700 =
      top.score === 720 &&
      scored.filter((c) => c.score === 720).length > 1;
    if (tied700) return '';

    if (top.score >= 500) return top.id;
    return '';
  };

  /** Exact city name match only — used so "Dubai" means the city, not a hotel name substring. */
  const resolveExactCityIdFromLocationText = async (raw) => {
    const trimmed = (raw || '').toString().trim();
    if (!trimmed) return '';
    const q = trimmed.toLowerCase();
    const normQ = normalizeText(trimmed);
    if (normQ.length < 2) return '';

    try {
      const cities = await g2TravelApi.getCities();
      if (!Array.isArray(cities) || cities.length === 0) return '';

      for (const city of cities) {
        const name = (city.name || city.cityName || city.city || '').toString().trim();
        if (!name) continue;
        const nid = city.id || city.cityId || city.code;
        if (!nid) continue;
        if (name.toLowerCase() === q || normalizeText(name) === normQ) {
          return String(nid).trim();
        }
      }
      return '';
    } catch {
      return '';
    }
  };

  const hotelMatchesLocation = (hotel, locationValue) => {
    const input = (locationValue || '').toString().trim().toLowerCase();
    if (!input) return true;

    const normalizedInput = normalizeText(input);
    const inputTokens = input.split(/\s+/).filter(Boolean);
    const normalizedTokens = inputTokens
      .map(normalizeText)
      .filter((t) => t && t.length >= 2);
    const inputCode = input.slice(0, 3).toUpperCase();

    const aliasCodes = [];
    if (normalizedInput === 'london') {
      aliasCodes.push('LON');
    }

    const fields = [
      hotel.name,
      hotel.address1,
      hotel.address2,
      hotel.city,
      hotel.cityName,
      hotel.cityCode,
      hotel.location,
      hotel.destination,
      hotel.destinationName,
      hotel.regionName,
      hotel.state,
      hotel.countryName,
      hotel.countryCode,
    ]
      .filter(Boolean)
      .map((v) => v.toString());

    const hasCodeMatch = fields.some((value) => {
      const upperValue = value.toUpperCase();
      return upperValue === inputCode || aliasCodes.includes(upperValue);
    });

    if (hasCodeMatch) {
      return true;
    }

    return fields.some((value) => {
      const lower = value.toString().toLowerCase();
      const normalized = normalizeText(value);

      if (lower.includes(input)) {
        return true;
      }

      if (
        normalized.length >= 4 &&
        normalizedInput.length >= 4 &&
        normalized.includes(normalizedInput)
      ) {
        return true;
      }

      if (
        normalized.length >= 4 &&
        normalizedInput.length >= 4 &&
        normalizedInput.includes(normalized) &&
        normalized.length >= Math.min(8, Math.max(4, Math.floor(normalizedInput.length * 0.6)))
      ) {
        return true;
      }

      if (
        normalizedTokens.length > 0 &&
        normalizedTokens.every((token) => normalized.includes(token))
      ) {
        return true;
      }

      return false;
    });
  };

  const resolveCityIdFromLocation = async (locationValue) => {
    const raw = (locationValue || '').toString().trim();
    if (!raw) return '';

    const cacheKey = normalizeText(raw);
    if (cityLookupCacheRef.current[cacheKey] !== undefined) {
      return cityLookupCacheRef.current[cacheKey];
    }

    try {
      const cities = await g2TravelApi.getCities();
      if (!Array.isArray(cities) || cities.length === 0) {
        cityLookupCacheRef.current[cacheKey] = '';
        return '';
      }

      const normalizedRaw = normalizeText(raw);
      const rawLower = raw.toLowerCase();
      const rawTokens = rawLower.split(/\s+/).filter(Boolean);

      const scored = cities
        .map((city) => {
          const id = city.id || city.cityId || city.code || '';
          if (!id) return null;

          const name = (city.name || city.cityName || city.city || '').toString();
          const code = (city.code || '').toString();
          const countryName = (city.countryName || city.countryCode || '').toString();
          const combined = `${name} ${countryName}`.trim();
          const normalizedCombined = normalizeText(combined);
          const normalizedName = normalizeText(name);
          const lowerCombined = combined.toLowerCase();
          const lowerName = name.toLowerCase();

          let score = 0;
          if (normalizedName === normalizedRaw) score += 120;
          if (normalizedCombined === normalizedRaw) score += 110;
          if (lowerName === rawLower) score += 100;
          if (lowerCombined === rawLower) score += 90;
          if (normalizedName.includes(normalizedRaw)) score += 70;
          if (normalizedCombined.includes(normalizedRaw)) score += 60;
          if (rawTokens.length > 0 && rawTokens.every((token) => lowerCombined.includes(token))) score += 40;
          if (code && code.toLowerCase() === rawLower) score += 80;

          return { id: id.toString(), score };
        })
        .filter(Boolean)
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score);

      const resolvedId = scored.length > 0 ? scored[0].id : '';
      cityLookupCacheRef.current[cacheKey] = resolvedId;
      return resolvedId;
    } catch (error) {
      console.warn('⚠️ Could not resolve cityId from location:', raw, error);
      cityLookupCacheRef.current[cacheKey] = '';
      return '';
    }
  };

  const fetchHotels = async (fetchKey) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching hotels with search params:', searchParams);

      let effectivePropertyId = (searchParams.propertyId || '').toString().trim();
      let effectiveCityId = (searchParams.cityId || '').toString().trim();
      const searchTarget = (searchParams.searchTarget || 'free').toLowerCase();
      const loc = (searchParams.location || '').trim();
      const explicitCitySearch = searchTarget === 'city' && !!effectiveCityId;
      const checkInDate = searchParams?.checkInDate || searchParams?.checkIn || '';
      const checkOutDate = searchParams?.checkOutDate || searchParams?.checkOut || '';
      const adults = searchParams?.adults || 1;
      const children = searchParams?.children || 0;
      const rooms = searchParams?.rooms || 1;
      const latitude = searchParams?.latitude;
      const longitude = searchParams?.longitude;
      const radiusKm = searchParams?.radiusKm;

      if (!effectivePropertyId && loc && !explicitCitySearch) {
        if (searchTarget !== 'hotel') {
          const exactCityId = await resolveExactCityIdFromLocationText(loc);
          if (exactCityId) {
            effectiveCityId = effectiveCityId || exactCityId;
          }
        }
        if (!effectiveCityId) {
          try {
            const broadResponse = await g2TravelApi.getAllHotels({});
            let catalog =
              broadResponse.success && Array.isArray(broadResponse.hotels)
                ? broadResponse.hotels
                : [];
            if (catalog.length === 0) {
              const fb = await g2TravelApi.getProperties();
              catalog = Array.isArray(fb) ? fb : [];
            }
            effectivePropertyId =
              findBestHotelPropertyIdFromQuery(loc, catalog) || '';
            if (effectivePropertyId) {
              console.log(
                '✅ Resolved hotel propertyId from search text:',
                effectivePropertyId,
                loc
              );
            }
          } catch (resolveErr) {
            console.warn('⚠️ Hotel name resolve from catalog failed:', resolveErr);
          }
        }
      }

      let allHotels = [];
      // When /hotels?cityId=… already returns city-scoped rows, do not re-filter client-side;
      // property rows often use city name / codes that never equal the UUID, which was trimming ~370 → ~8.
      let skipPostFetchCityLocationFilter = false;

      try {
        if (effectivePropertyId) {
          try {
            const hotelDetails = await g2TravelApi.getHotelDetails(effectivePropertyId);
            if (hotelDetails.success && hotelDetails.hotel) {
              allHotels = [hotelDetails.hotel];
            }
          } catch (err) {
            console.error('Error fetching specific property:', err);
          }
        } else {
          if (!effectiveCityId && loc) {
            effectiveCityId = await resolveCityIdFromLocation(loc);
          }

          const apiFilters = {};
          if (effectiveCityId) {
            apiFilters.cityId = effectiveCityId;
          }
          if (loc) {
            apiFilters.location = loc;
          }
          const response = await g2TravelApi.getCombinedHotels({
            ...apiFilters,
            includeDuffel: true,
            checkInDate,
            checkOutDate,
            adults,
            children,
            rooms,
            latitude,
            longitude,
            radiusKm,
          });
          
          if (response.success && response.hotels && response.hotels.length > 0) {
            allHotels = response.hotels;
            if (apiFilters.cityId) {
              skipPostFetchCityLocationFilter = true;
            }
          } else {
            const fallbackData = await g2TravelApi.getProperties();
            if (fallbackData && fallbackData.length > 0) {
              allHotels = fallbackData;
            }
          }

          // Some backends return only a small page (e.g. 10) when cityId is used.
          // If that happens, broaden the fetch and apply client-side city/location filtering.
          if (effectiveCityId && allHotels.length > 0 && allHotels.length <= 10) {
            try {
              console.warn(
                `⚠️ cityId query returned only ${allHotels.length} hotels. Trying broader fetch fallback...`
              );
              const broadResponse = await g2TravelApi.getCombinedHotels({
                includeDuffel: true,
                checkInDate,
                checkOutDate,
                adults,
                children,
                rooms,
                latitude,
                longitude,
                radiusKm,
              });
              if (broadResponse.success && Array.isArray(broadResponse.hotels) && broadResponse.hotels.length > 0) {
                const cityIdNormalized = effectiveCityId.toString().trim().toLowerCase();
                const locationValue = searchParams.location || '';

                const broadened = broadResponse.hotels.filter((hotel) => {
                  const hotelCityId = (hotel.cityId || '').toString().trim().toLowerCase();
                  const hotelCityExtra = (
                    hotel.city?.id ||
                    hotel.city?.cityId ||
                    ''
                  ).toString().trim().toLowerCase();
                  const hotelCity = (hotel.city || hotel.cityName || '').toString().trim().toLowerCase();
                  const hotelCityCode = (hotel.cityCode || '').toString().trim().toLowerCase();
                  const idMatch =
                    hotelCityId === cityIdNormalized ||
                    hotelCityExtra === cityIdNormalized ||
                    hotelCity === cityIdNormalized ||
                    hotelCityCode === cityIdNormalized;
                  const textMatch = locationValue ? hotelMatchesLocation(hotel, locationValue) : false;
                  return idMatch || textMatch;
                });

                if (broadened.length > allHotels.length) {
                  console.log(
                    `✅ Broader city fallback improved results: ${allHotels.length} -> ${broadened.length}`
                  );
                  allHotels = broadened;
                  skipPostFetchCityLocationFilter = true;
                }
              }
            } catch (fallbackErr) {
              console.warn('⚠️ Broader city fallback failed:', fallbackErr);
            }
          }
        }
        
        if (!skipPostFetchCityLocationFilter && !effectivePropertyId) {
          const postFetchCityId = searchParams.cityId || await resolveCityIdFromLocation(searchParams.location);
          if (postFetchCityId && allHotels.length > 0) {
            const cityIdNormalized = postFetchCityId.toString().trim().toLowerCase();
            allHotels = allHotels.filter((hotel) => {
              const hotelCityId = (hotel.cityId || '').toString().trim().toLowerCase();
              const hotelCityExtra = (
                hotel.city?.id ||
                hotel.city?.cityId ||
                ''
              ).toString().trim().toLowerCase();
              const hotelCity = (hotel.city || hotel.cityName || '').toString().trim().toLowerCase();
              const hotelCityCode = (hotel.cityCode || '').toString().trim().toLowerCase();
              return (
                hotelCityId === cityIdNormalized ||
                hotelCityExtra === cityIdNormalized ||
                hotelCity === cityIdNormalized ||
                hotelCityCode === cityIdNormalized
              );
            });
          }

          if (searchParams.location && allHotels.length > 0) {
            allHotels = allHotels.filter((hotel) => hotelMatchesLocation(hotel, searchParams.location));
          }
        }
        
        setHotels(allHotels);

        const emptyMsg =
          'No hotels found for your search criteria. Please try different dates or location.';
        mergeHotelListingCache(fetchKey, {
          hotels: allHotels,
          error: allHotels.length === 0 ? emptyMsg : null,
          hotelPrices: {},
        });

        // Fetch prices for all hotels, then show only hotels with available rooms.
        await fetchHotelPrices(allHotels, fetchKey);

        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);

        if (allHotels.length === 0) {
          setError(emptyMsg);
        }
        
      } catch (primaryError) {
        if (primaryError.message.includes('Rate limit') || primaryError.message.includes('429')) {
          try {
            const fallbackData = await g2TravelApi.getProperties();
            if (fallbackData && fallbackData.length > 0) {
              setHotels(fallbackData);
              mergeHotelListingCache(fetchKey, {
                hotels: fallbackData,
                error: null,
                hotelPrices: {},
              });
              await fetchHotelPrices(fallbackData, fetchKey);
              return;
            }
          } catch (fallbackError) {
            console.error('❌ Fallback also failed:', fallbackError);
          }
        }
        throw primaryError;
      }
      
    } catch (err) {
      console.error('❌ Error:', err);
      let errorMessage = 'Failed to load hotels';
      
      if (err.message.includes('Rate limit') || err.message.includes('429')) {
        errorMessage = 'Too many requests. Please wait a moment and refresh the page.';
      } else if (err.message.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = err.message || 'Failed to load hotels';
      }
      
      setError(errorMessage);
      setHotels([]);
      mergeHotelListingCache(fetchKey, {
        hotels: [],
        error: errorMessage,
        hotelPrices: {},
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch prices for hotels in background
  const fetchHotelPrices = async (hotelsList, listingCacheKey = null, overrideCurrency = null) => {
    setPricesLoading(true);
    try {
      const prices = {};
      const checkInDate = searchParams?.checkInDate || searchParams?.checkIn || null;
      const nights = searchParams?.nights || 1;
      const adults = searchParams?.adults || 1;
      const children = searchParams?.children || 0;
      const nationality = filters.nationality || searchParams?.nationality || 'GB';
      // Keep provider/API request stable in GBP, then convert in UI using CurrencyContext.
      const currency = 'GBP';

      // Check availability/price for every listed hotel so unavailable ones can be filtered out.
      const hotelsToPrice = Array.isArray(hotelsList) ? hotelsList : [];

      // Avoid hammering the backend with hundreds of simultaneous price requests.
      const BATCH_SIZE = 12;
      for (let i = 0; i < hotelsToPrice.length; i += BATCH_SIZE) {
        const batch = hotelsToPrice.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (hotel) => {
          try {
            const hotelId = hotelPrimaryId(hotel);
            if (hotelId && checkInDate) {
              let priceData = await g2TravelApi.getMinimumPrice(
                hotelId,
                checkInDate,
                nights,
                adults,
                children,
                nationality,
                currency
              );
              // Some nationalities can return no room options for a property even when it is bookable.
              // Fallback to GB to keep result coverage stable and only change display currency.
              if ((!priceData || !priceData.minPrice) && nationality !== 'GB') {
                priceData = await g2TravelApi.getMinimumPrice(
                  hotelId,
                  checkInDate,
                  nights,
                  adults,
                  children,
                  'GB',
                  currency
                );
              }
              if (priceData && priceData.minPrice) {
                prices[hotelId] = priceData.minPrice;
              }
            }
          } catch (err) {
            // Silently fail for individual price fetches
            console.warn(`Failed to fetch price for hotel ${hotel.id || hotel.name}`);
          }
          })
        );
      }

      setHotelPrices(prices);
      if (listingCacheKey) {
        mergeHotelListingCache(listingCacheKey, { hotelPrices: prices, hotels: hotelsList });
      }
      return prices;
    } finally {
      setPricesLoading(false);
    }
  };

  const getHotelPrice = useCallback((hotel) => {
    const hotelId = hotelPrimaryId(hotel);
    if (!hotelId) return null;
    return hotelPrices[hotelId] ?? null;
  }, [hotelPrices]);

  const blobMatchesLabelWords = (blob, label) => {
    if (!label || !blob) return false;
    const words = label
      .toLowerCase()
      .split(/\s+/)
      .map((w) => w.replace(/[^a-z0-9]/g, ''))
      .filter((w) => w.length > 2);
    if (words.length === 0) return blob.includes(label.toLowerCase());
    return words.every((w) => blob.includes(w));
  };

  // Helper function to check if hotel matches property type
  const matchesPropertyType = (hotel, propertyTypes) => {
    if (!propertyTypes || propertyTypes.length === 0) return true;

    const name = (hotel.name || '').toLowerCase();
    const description = ((hotel.description || hotel.overview || '') + '').toLowerCase();
    const combined = `${name} ${description}`;

    const apartmentSignals =
      /apartment|aparthotel|self-catering|self catering|kitchenette|rental unit|serviced apartment/i.test(
        combined
      );
    const hotelSignals =
      /hotel|\binns?\b|resort|lodge|hostel|suites?\b|palace|marriott|hilton|hyatt|sheraton|holiday inn|ibis|mercure|novotel|pullman|westin|wyndham|radisson|intercontinental|sofitel|melia|crowne/i.test(
        combined
      );

    const wantsHotel = propertyTypes.includes('hotel');
    const wantsApartment = propertyTypes.includes('apartment');

    if (wantsHotel && wantsApartment) return true;
    if (wantsApartment && !wantsHotel) return apartmentSignals;
    if (wantsHotel && !wantsApartment) {
      if (apartmentSignals && !hotelSignals) return false;
      return true;
    }
    return false;
  };

  // Filter hotels based on selected filters (memoized to prevent infinite loops)
  const filteredHotels = useMemo(
    () =>
      hotels
        .filter((hotel) => {
          const blob = getHotelSearchBlob(hotel);
          const hotelPrice = getHotelPrice(hotel);

          // Strict availability: only show hotels with at least one priced room option.
          if (hotelPrice === null) {
            return false;
          }

          // Search term filter
          if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            const hotelName = (hotel.name || '').toLowerCase();
            const address = ((hotel.address1 || '') + ' ' + (hotel.address2 || '')).toLowerCase();
            const city = (hotel.cityName || hotel.city || '').toLowerCase();
            const country = (hotel.countryName || hotel.countryCode || '').toLowerCase();

            if (
              !hotelName.includes(searchLower) &&
              !address.includes(searchLower) &&
              !city.includes(searchLower) &&
              !country.includes(searchLower)
            ) {
              return false;
            }
          }

          // Price filter: slider max is "£{DEFAULT_MAX_PRICE}+" — at the right end, do not cap
          if (filters.priceRange && filters.priceRange.length === 2) {
            const [minP, maxP] = filters.priceRange;
            const maxCapActive = maxP < DEFAULT_MAX_PRICE;
            const priceRestrictionActive = minP > 0 || maxCapActive;
            if (priceRestrictionActive && hotelPrice === null) {
              return false;
            }
            if (hotelPrice !== null) {
              if (hotelPrice < minP) return false;
              if (maxCapActive && hotelPrice > maxP) return false;
            }
          }

          // Star rating filter
          if (filters.starRating && filters.starRating.length > 0) {
            const hotelStarRating = getHotelStarRating(hotel);
            if (!filters.starRating.includes(hotelStarRating)) {
              return false;
            }
          }

          // Property type filter
          if (filters.propertyType && filters.propertyType.length > 0) {
            if (!matchesPropertyType(hotel, filters.propertyType)) {
              return false;
            }
          }

          if (!hotelMatchesReviewScoreFilters(hotel, filters.reviewScore)) {
            return false;
          }

          // Country filter (if countryCode is in filters)
          if (filters.countryCode && filters.countryCode.length > 0) {
            if (!filters.countryCode.includes(hotel.countryCode)) {
              return false;
            }
          }

          // Facilities (property-level)
          const facilities = filters.facilities || {};
          for (const key of Object.keys(facilities)) {
            if (!facilities[key]) continue;
            if (!hotelMatchesKeywordFacility(hotel, key)) return false;
          }

          const roomFacilities = filters.roomFacilities || {};
          for (const key of Object.keys(roomFacilities)) {
            if (!roomFacilities[key]) continue;
            if (!hotelMatchesKeywordFacility(hotel, key)) return false;
          }

          const brands = filters.brands || [];
          if (brands.length > 0) {
            const nameLower = (hotel.name || hotel.propertyName || '').toLowerCase();
            const hit = brands.some((b) => nameLower.includes((b || '').toLowerCase()));
            if (!hit) return false;
          }

          const meals = filters.meals || [];
          for (const m of meals) {
            const kws = MEAL_KEYWORDS[m];
            if (!kws || !kws.some((kw) => blob.includes(kw))) return false;
          }

          const travelGroup = filters.travelGroup || [];
          for (const g of travelGroup) {
            const kws = TRAVEL_GROUP_KEYWORDS[g];
            if (!kws || !kws.some((kw) => blob.includes(kw))) return false;
          }

          const bedPreference = filters.bedPreference || [];
          for (const bed of bedPreference) {
            const kws = BED_KEYWORDS[bed];
            if (!kws || !kws.some((kw) => blob.includes(kw))) return false;
          }

          const landmarks = filters.landmarks || [];
          for (const lm of landmarks) {
            if (!blobMatchesLabelWords(blob, lm)) return false;
          }

          const neighborhood = filters.neighborhood || [];
          for (const nb of neighborhood) {
            if (!blobMatchesLabelWords(blob, nb)) return false;
          }

          const funThings = filters.funThings || [];
          for (const ft of funThings) {
            const kws = FUN_THING_KEYWORDS[ft];
            if (!kws || !kws.some((kw) => blob.includes(kw))) return false;
          }

          const certifications = filters.certifications || [];
          if (certifications.includes('sustainability')) {
            const ok =
              /sustainab|eco-?certif|green key|earthcheck|leed|green tourism|travel sustainable/i.test(blob);
            if (!ok) return false;
          }

          const propAcc = filters.propertyAccessibility || {};
          for (const key of Object.keys(propAcc)) {
            if (!propAcc[key]) continue;
            const phrase = key.replace(/([A-Z])/g, ' $1').toLowerCase();
            const tokens = phrase.split(/\s+/).filter((t) => t.length > 3);
            if (tokens.length === 0 || !tokens.some((t) => blob.includes(t))) return false;
          }

          const roomAcc = filters.roomAccessibility || {};
          for (const key of Object.keys(roomAcc)) {
            if (!roomAcc[key]) continue;
            const phrase = key.replace(/([A-Z])/g, ' $1').toLowerCase();
            const tokens = phrase.split(/\s+/).filter((t) => t.length > 3);
            if (tokens.length === 0 || !tokens.some((t) => blob.includes(t))) return false;
          }

          return true;
        })
        .sort((a, b) => {
          switch (sortBy) {
            case 'price-low': {
              const priceA = getHotelPrice(a);
              const priceB = getHotelPrice(b);
              const aVal = priceA != null ? priceA : Infinity;
              const bVal = priceB != null ? priceB : Infinity;
              return aVal - bVal;
            }
            case 'price-high': {
              const priceA = getHotelPrice(a);
              const priceB = getHotelPrice(b);
              const aVal = priceA != null ? priceA : 0;
              const bVal = priceB != null ? priceB : 0;
              return bVal - aVal;
            }
            case 'rating': {
              const ratingA = getHotelStarRating(a);
              const ratingB = getHotelStarRating(b);
              return ratingB - ratingA;
            }
            case 'guest-rating': {
              const ratingA = getHotelGuestScoreForFilter(a);
              const ratingB = getHotelGuestScoreForFilter(b);
              const aVal = ratingA != null ? ratingA : -1;
              const bVal = ratingB != null ? ratingB : -1;
              return bVal - aVal;
            }
            case 'name':
              return (a.name || '').localeCompare(b.name || '');
            default:
              return 0;
          }
        }),
    [hotels, searchTerm, filters, sortBy, getHotelPrice]
  );

  // Always show provider counts from the currently displayed result set.
  const providerCounts = useMemo(() => {
    const source = Array.isArray(filteredHotels) ? filteredHotels : [];
    const duffel = source.filter((hotel) => {
      if (!hotel) return false;
      if ((hotel.provider || '').toString().toLowerCase() === 'duffel') return true;
      const id = hotel.id != null ? String(hotel.id) : '';
      return id.startsWith('duffel_');
    }).length;
    const g2 = Math.max(0, source.length - duffel);
    return { g2, duffel };
  }, [filteredHotels]);

  /** Must include listing identity (location, cityId, …) so /hotel/…?… matches the cache key on "Back to results". */
  const hotelDetailSearchParams = useMemo(() => {
    const queryParams = new URLSearchParams();
    const sp = searchParams || {};

    if (sp.location) queryParams.set('location', String(sp.location));
    if (sp.cityId) queryParams.set('cityId', String(sp.cityId));
    if (sp.propertyId) queryParams.set('propertyId', String(sp.propertyId));
    if (sp.searchTarget) queryParams.set('searchTarget', String(sp.searchTarget));

    const checkInVal = sp.checkInDate || sp.checkIn;
    if (checkInVal) queryParams.set('checkIn', String(checkInVal));
    const checkOutVal = sp.checkOut || sp.checkOutDate;
    if (checkOutVal) queryParams.set('checkOut', String(checkOutVal));
    if (sp.nights != null && sp.nights !== '') queryParams.set('nights', String(sp.nights));
    if (sp.adults != null && sp.adults !== '') queryParams.set('adults', String(sp.adults));
    if (sp.children != null && sp.children !== '') {
      queryParams.set('children', String(sp.children));
    }
    if (sp.guests != null && sp.guests !== '') queryParams.set('guests', String(sp.guests));
    if (filters.nationality) queryParams.set('nationality', String(filters.nationality));
    else if (sp.nationality) queryParams.set('nationality', String(sp.nationality));
    if (sp.currency) queryParams.set('currency', String(sp.currency));

    return queryParams;
  }, [
    searchParams?.location,
    searchParams?.cityId,
    searchParams?.propertyId,
    searchParams?.searchTarget,
    searchParams?.checkIn,
    searchParams?.checkInDate,
    searchParams?.checkOut,
    searchParams?.checkOutDate,
    searchParams?.nights,
    searchParams?.adults,
    searchParams?.children,
    searchParams?.guests,
    searchParams?.nationality,
    filters.nationality,
    searchParams?.currency,
  ]);

  const searchLocation = searchParams.location || 'London';
  const locationParts = searchLocation.split(',').map(part => part.trim());

  return (
    <div className="hotels-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="hotels-main">
        <Box className="hotels-container">
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" className="hotels-breadcrumbs">
            <Link to="/" className="hotels-breadcrumb-link">Home</Link>
            <Link to="/hotels" className="hotels-breadcrumb-link">United Kingdom</Link>
            {locationParts.length > 1 && (
              <Link to="/hotels" className="hotels-breadcrumb-link">{locationParts[0]}</Link>
            )}
            <Typography className="hotels-breadcrumb-current">{locationParts[locationParts.length - 1]}</Typography>
          </Breadcrumbs>

          {/* Search Criteria Display */}
          {searchParams && (searchParams.location || searchParams.checkInDate || searchParams.checkIn) && (
            <Box
              className="hotels-search-summary"
              sx={{ 
              mb: 3, 
              p: 2.5, 
              backgroundColor: '#E6F0FF', 
              borderRadius: 2,
              border: '1px solid #B3D1FF'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#003580' }}>
                    Search Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchParams.location && `Location: ${searchParams.location} • `}
                    Nationality: {filters.nationality || searchParams.nationality || 'GB'} • 
                    Check-in: {new Date(searchParams.checkInDate || searchParams.checkIn || new Date()).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })} • 
                    {searchParams.nights || 1} {searchParams.nights === 1 ? 'night' : 'nights'} • 
                    {searchParams.adults || 1} {searchParams.adults === 1 ? 'adult' : 'adults'}
                    {searchParams.children > 0 && `, ${searchParams.children} ${searchParams.children === 1 ? 'child' : 'children'}`}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75, color: '#003580', fontWeight: 600 }}>
                    Providers: G2 {providerCounts.g2} hotel{providerCounts.g2 === 1 ? '' : 's'} • Duffel {providerCounts.duffel} hotel{providerCounts.duffel === 1 ? '' : 's'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, color: '#4f5b6a' }}>
                    Showing {filteredHotels.length} of {hotels.length} loaded hotels (after filters/availability checks)
                  </Typography>
                </Box>
                <Button 
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/')}
                  sx={{
                    borderColor: '#003580',
                    color: '#003580',
                    '&:hover': {
                      borderColor: '#002a5c',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Modify Search
                </Button>
              </Box>
            </Box>
          )}

          {/* Top Section */}
          <Box className="hotels-top-section">
            <Box className="hotels-header">
              <Typography className="hotels-title">
                {searchParams?.location 
                  ? `${searchParams.location}: ${filteredHotels.length} ${filteredHotels.length === 1 ? 'property' : 'properties'} found`
                  : `${filteredHotels.length} ${filteredHotels.length === 1 ? 'property' : 'properties'} found`
                }
              </Typography>
              <Box className="hotels-controls">
                <TextField
                  placeholder="Search hotels..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="hotels-search"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setMobileFiltersOpen(true)}
                  className="hotels-filters-button-mobile"
                >
                  Filters
                </Button>
                <TextField
                  select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="hotels-sort"
                  size="small"
                >
                  <MenuItem value="recommended">Recommended</MenuItem>
                  <MenuItem value="price-low">Price (Low to High)</MenuItem>
                  <MenuItem value="price-high">Price (High to Low)</MenuItem>
                  <MenuItem value="rating">Star Rating (High to Low)</MenuItem>
                  <MenuItem value="guest-rating">Guest Rating</MenuItem>
                  <MenuItem value="name">Name (A-Z)</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>

          {/* Main Content - Two Column Layout */}
          <Box className="hotels-content">
            {/* Filters Sidebar */}
            <Box className="hotels-sidebar-wrapper">
              <FiltersSidebar filters={filters} onFilterChange={setFilters} hotels={hotels} />
            </Box>

            {/* Results Section */}
            <Box className="hotels-results">
              {loading || pricesLoading ? (
                <Box className="hotels-loading">
                  <CircularProgress size={50} />
                </Box>
              ) : error ? (
                <Alert severity="error" className="hotels-error">
                  {error}
                </Alert>
              ) : (
                <>
                  {filteredHotels.length === 0 ? (
                    <Box className="hotels-empty">
                      <Typography variant="h6" className="hotels-empty-title">
                        No hotels found
                      </Typography>
                      <Typography variant="body2" className="hotels-empty-text">
                        Try adjusting your filters or search terms
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setFilters({
                            priceRange: [0, DEFAULT_MAX_PRICE],
                            nationality: searchParams?.nationality || 'GB',
                            propertyType: [],
                            starRating: [],
                            reviewScore: [],
                            amenities: {},
                            facilities: {},
                            landmarks: [],
                            bedPreference: [],
                            meals: [],
                            neighborhood: [],
                            brands: [],
                            travelGroup: [],
                            distance: [],
                            funThings: [],
                            certifications: [],
                            propertyAccessibility: {},
                            roomAccessibility: {},
                            roomFacilities: {},
                          });
                          setSearchTerm('');
                        }}
                        className="hotels-clear-button"
                      >
                        Clear All Filters
                      </Button>
                    </Box>
                  ) : (
                    <Box className="hotels-list">
                      {filteredHotels.map((hotel, index) => {
                        const hotelId = hotel.id || hotel.propertyId || hotel.code || `hotel-${index}`;
                        const detailPath = buildHotelDetailPath(hotelId, hotelDetailSearchParams);
                        return (
                          <HotelCardHorizontal
                            key={`${hotelId}-${index}`}
                            hotel={hotel}
                            detailTo={detailPath}
                            linkState={{ hotel }}
                            prefetchedPrice={getHotelPrice(hotel)}
                            prefetchedCurrency="GBP"
                            searchParams={{
                              ...searchParams,
                              nationality: filters.nationality,
                              currency: getCurrencyForNationality(filters.nationality),
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </main>

      <Footer />

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="left"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        className="hotels-filters-drawer"
        sx={{ zIndex: 1400 }}
      >
        <Box className="hotels-filters-drawer-content">
          <Box className="hotels-filters-drawer-header">
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setMobileFiltersOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <FiltersSidebar filters={filters} onFilterChange={setFilters} hotels={hotels} />
        </Box>
      </Drawer>

    </div>
  );
};

export default Hotels;
